import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui";
import { useCookieConsent } from "./useCookieConsent";
import { CookiePreferencesModal } from "./CookiePreferencesModal";

/**
 * Cookie consent banner shown on the authenticated app's first visit.
 *
 * Mirrors the hotsite banner UX:
 * - Slides up from the bottom-right.
 * - Three CTAs: Aceitar todos / Apenas necessários / Personalizar.
 * - Persists via the shared `useCookieConsent` hook.
 */
export function CookieBanner() {
  const {
    consent,
    hydrated,
    hasDecided,
    acceptAll,
    acceptEssentialOnly,
    setConsent,
  } = useCookieConsent();
  const [prefsOpen, setPrefsOpen] = useState(false);

  if (!hydrated) return null;
  if (hasDecided && !prefsOpen) return null;

  return (
    <>
      <div
        role="dialog"
        aria-live="polite"
        aria-label="Aviso de uso de cookies"
        className="fixed bottom-4 left-4 right-4 z-[1024] mx-auto w-auto max-w-3xl rounded-2xl border border-zinc-200 bg-white p-5 shadow-2xl shadow-zinc-500/30 md:bottom-6 md:left-auto md:right-6 md:p-6"
      >
        <div className="space-y-2">
          <h2 className="text-base font-semibold text-zinc-800 md:text-lg">
            Cookies e privacidade
          </h2>
          <p className="text-sm leading-relaxed text-zinc-600">
            Usamos cookies para melhorar sua experiência. Você pode escolher
            quais aceitar. Saiba mais na nossa{" "}
            <Link
              to="/legal/privacidade"
              className="font-medium text-teal-600 underline underline-offset-2"
            >
              Política de Privacidade
            </Link>
            .
          </p>
        </div>
        <div className="mt-4 flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:justify-end">
          <Button
            variant="ghost"
            className="text-sm"
            onClick={() => setPrefsOpen(true)}
          >
            Personalizar
          </Button>
          <Button
            variant="outline"
            className="text-sm"
            onClick={() => acceptEssentialOnly()}
          >
            Apenas necessários
          </Button>
          <Button className="text-sm" onClick={() => acceptAll()}>
            Aceitar todos
          </Button>
        </div>
      </div>

      <CookiePreferencesModal
        open={prefsOpen}
        onOpenChange={setPrefsOpen}
        initial={consent}
        onSave={(c) => setConsent(c)}
      />
    </>
  );
}
